import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import {
    Card,
    CardHeader,
    CardBody,
    Button,
    Input,
    Row,
    Col,
} from 'reactstrap';

const TeamPlayers = ({ teamId }) => {
    const [players, setPlayers] = useState([]);
    const [newPlayer, setNewPlayer] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        alternate_id: '',
        notes: '',
        team: teamId || '',
    });
    const [editingId, setEditingId] = useState(null);
    const [editedPlayer, setEditedPlayer] = useState({});

    const fetchPlayers = useCallback(async () => {
        try {
            const response = await api.get(`/team-players/?team=${teamId}`);
            setPlayers(response.data);
        } catch (error) {
            console.error('Error fetching players:', error);
        }
    }, [teamId]);

    useEffect(() => {
        if (teamId) fetchPlayers();
    }, [teamId, fetchPlayers]);

    const addPlayer = async () => {
        try {
            await api.post('/team-players/', { ...newPlayer, team: teamId });
            setNewPlayer({
                first_name: '',
                last_name: '',
                email: '',
                phone_number: '',
                alternate_id: '',
                notes: '',
                team: teamId,
            });
            fetchPlayers();
        } catch (error) {
            console.error('Error adding player:', error);
        }
    };

    const deletePlayer = async (id) => {
        try {
            await api.delete(`/team-players/${id}/`);
            fetchPlayers();
        } catch (error) {
            console.error('Error deleting player:', error);
        }
    };

    const startEditing = (player) => {
        setEditingId(player.id);
        setEditedPlayer({ ...player });
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditedPlayer({});
    };

    const saveEditedPlayer = async () => {
        try {
            await api.put(`/team-players/${editingId}/`, editedPlayer);
            setEditingId(null);
            setEditedPlayer({});
            fetchPlayers();
        } catch (error) {
            console.error('Error saving player:', error);
        }
    };

    const togglePaidStatus = async (player) => {
        try {
            await api.put(`/team-players/${player.id}/`, { 
                ...player,
                paid: !player.paid 
            });
            fetchPlayers();
        } catch (error) {
            console.error('Error updating paid status:', error);
        }
    };

    const togglePaidPrizeStatus = async (player) => {
        try {
            await api.put(`/team-players/${player.id}/`, {
                ...player,
                paid_prize: !player.paid_prize
            });
            fetchPlayers();
        } catch (error) {
            console.error('Error updating prize paid status:', error);
        }
    };

    const toggleAllPaid = async () => {
        try {
            const allPaid = players.every(p => p.paid);
            const updatePromises = players.map(player => 
                api.put(`/team-players/${player.id}/`, { 
                    ...player,
                    paid: !allPaid 
                })
            );
            await Promise.all(updatePromises);
            fetchPlayers();
        } catch (error) {
            console.error('Error updating all paid statuses:', error);
        }
    };

    const toggleAllPaidPrize = async () => {
        try {
            const allPaidPrize = players.every(p => p.paid_prize);
            const updatePromises = players.map(player =>
                api.put(`/team-players/${player.id}/`, {
                    ...player,
                    paid_prize: !allPaidPrize
                })
            );
            await Promise.all(updatePromises);
            fetchPlayers();
        } catch (error) {
            console.error('Error updating all prize paid statuses:', error);
        }
    };

    return (
        <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">Team Players</h3>
                {players.length > 0 && (
                    <div className="d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center">
                            <label className="me-2 mb-0 fw-bold" style={{ fontSize: '0.95rem' }}>
                                <i className="fas fa-dollar-sign me-1"></i>Mark All Paid
                            </label>
                            <div className="form-check form-switch mb-0">
                                <Input
                                    type="switch"
                                    id="paidToggleAll"
                                    checked={players.every(p => p.paid)}
                                    onChange={toggleAllPaid}
                                    style={{ cursor: 'pointer', width: '3rem', height: '1.5rem' }}
                                />
                            </div>
                        </div>
                        <div className="d-flex align-items-center">
                            <label className="me-2 mb-0 fw-bold" style={{ fontSize: '0.95rem' }}>
                                <i className="fas fa-trophy me-1"></i>Mark All Prize Paid
                            </label>
                            <div className="form-check form-switch mb-0">
                                <Input
                                    type="switch"
                                    id="paidPrizeToggleAll"
                                    checked={players.every(p => p.paid_prize)}
                                    onChange={toggleAllPaidPrize}
                                    style={{ cursor: 'pointer', width: '3rem', height: '1.5rem' }}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </CardHeader>
            <CardBody>
                {players.length === 0 && (
                    <p className="text-muted">No players found</p>
                )}

                {players.map((player) => (
                    <Card key={player.id} className="mb-3">
                        <CardBody>
                            <Row className="align-items-center">
                                <Col xs={2}>
                                    {editingId === player.id ? (
                                        <Input
                                            value={editedPlayer.first_name || ''}
                                            onChange={(e) =>
                                                setEditedPlayer((prev) => ({
                                                    ...prev,
                                                    first_name: e.target.value,
                                                }))
                                            }
                                            placeholder="First Name"
                                        />
                                    ) : (
                                        <span>{player.first_name}</span>
                                    )}
                                </Col>
                                <Col xs={2}>
                                    {editingId === player.id ? (
                                        <Input
                                            value={editedPlayer.last_name || ''}
                                            onChange={(e) =>
                                                setEditedPlayer((prev) => ({
                                                    ...prev,
                                                    last_name: e.target.value,
                                                }))
                                            }
                                            placeholder="Last Name"
                                        />
                                    ) : (
                                        <span>{player.last_name}</span>
                                    )}
                                </Col>
                                <Col xs={3}>
                                    {editingId === player.id ? (
                                        <Input
                                            type="email"
                                            value={editedPlayer.email || ''}
                                            onChange={(e) =>
                                                setEditedPlayer((prev) => ({
                                                    ...prev,
                                                    email: e.target.value,
                                                }))
                                            }
                                            placeholder="Email"
                                        />
                                    ) : (
                                        <span>{player.email}</span>
                                    )}
                                </Col>
                                <Col xs={2}>
                                    {editingId === player.id ? (
                                        <Input
                                            value={editedPlayer.phone_number || ''}
                                            onChange={(e) =>
                                                setEditedPlayer((prev) => ({
                                                    ...prev,
                                                    phone_number: e.target.value,
                                                }))
                                            }
                                            placeholder="Phone"
                                        />
                                    ) : (
                                        <span>{player.phone_number}</span>
                                    )}
                                </Col>
                                <Col xs={2}>
                                    {editingId === player.id ? (
                                        <Input
                                            value={editedPlayer.alternate_id || ''}
                                            onChange={(e) =>
                                                setEditedPlayer((prev) => ({
                                                    ...prev,
                                                    alternate_id: e.target.value,
                                                }))
                                            }
                                            placeholder="AlternateID"
                                        />
                                    ) : (
                                        <span>{player.alternate_id}</span>
                                    )}
                                </Col>
                                <Col xs={1}>
                                    {editingId === player.id ? (
                                        <Input
                                            value={editedPlayer.tees || ''}
                                            onChange={(e) =>
                                                setEditedPlayer((prev) => ({
                                                    ...prev,
                                                    tees: e.target.value,
                                                }))
                                            }
                                            placeholder="Tees"
                                        />
                                    ) : (
                                        <span>{player.tees}</span>
                                    )}
                                </Col>
                            </Row>

                            <Row className="mt-3">
                                <Col>
                                    {editingId === player.id ? (
                                        <Input
                                            type="textarea"
                                            value={editedPlayer.notes || ''}
                                            onChange={(e) =>
                                                setEditedPlayer((prev) => ({
                                                    ...prev,
                                                    notes: e.target.value,
                                                }))
                                            }
                                            placeholder="Notes"
                                        />
                                    ) : (
                                        <div className="text-muted">
                                            Notes: {player.notes || 'N/A'}
                                        </div>
                                    )}
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col xs={9}>
                                    {editingId === player.id ? (
                                        <>
                                            <Button
                                                color="success"
                                                size="sm"
                                                onClick={saveEditedPlayer}
                                                className="me-1"
                                            >
                                                Save
                                            </Button>
                                            <Button color="secondary" size="sm" onClick={cancelEditing}>
                                                Cancel
                                            </Button>
                                        </>
                                    ) : (
                                        <>
                                            <Button
                                                color="primary"
                                                size="sm"
                                                className="me-1"
                                                onClick={() => startEditing(player)}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                color="danger"
                                                size="sm"
                                                onClick={() => deletePlayer(player.id)}
                                            >
                                                Delete
                                            </Button>
                                        </>
                                    )}
                                </Col>
                                <Col xs={3} className="text-end d-flex align-items-center justify-content-end gap-3">
                                    <div className="d-flex align-items-center">
                                        <label className="me-2 mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>
                                            <i className="fas fa-dollar-sign me-1"></i>Paid
                                        </label>
                                        <div className="form-check form-switch mb-0">
                                            <Input
                                                type="switch"
                                                id={`paidToggle-${player.id}`}
                                                checked={player.paid || false}
                                                onChange={() => togglePaidStatus(player)}
                                                style={{ cursor: 'pointer', width: '3rem', height: '1.5rem' }}
                                            />
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <label className="me-2 mb-0 fw-bold" style={{ fontSize: '0.9rem' }}>
                                            <i className="fas fa-trophy me-1"></i>Prize
                                        </label>
                                        <div className="form-check form-switch mb-0">
                                            <Input
                                                type="switch"
                                                id={`paidPrizeToggle-${player.id}`}
                                                checked={player.paid_prize || false}
                                                onChange={() => togglePaidPrizeStatus(player)}
                                                style={{ cursor: 'pointer', width: '3rem', height: '1.5rem' }}
                                            />
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                ))}

                {/* Add-new-player card if there are less than two players*/}
                {players.length < 2 && (
                    <Card className="mt-4">
                        <CardHeader tag="h5">Add New Player</CardHeader>
                        <CardBody>
                            <Row className="mb-2">
                                <Col md={3}>
                                    <Input
                                        value={newPlayer.first_name}
                                        onChange={(e) =>
                                            setNewPlayer((prev) => ({
                                                ...prev,
                                                first_name: e.target.value,
                                            }))
                                        }
                                        placeholder="First Name"
                                    />
                                </Col>
                                <Col md={3}>
                                    <Input
                                        value={newPlayer.last_name}
                                        onChange={(e) =>
                                            setNewPlayer((prev) => ({
                                                ...prev,
                                                last_name: e.target.value,
                                            }))
                                        }
                                        placeholder="Last Name"
                                    />
                                </Col>
                                <Col md={3}>
                                    <Input
                                        type="email"
                                        value={newPlayer.email}
                                        onChange={(e) =>
                                            setNewPlayer((prev) => ({
                                                ...prev,
                                                email: e.target.value,
                                            }))
                                        }
                                        placeholder="Email"
                                    />
                                </Col>
                                <Col md={2}>
                                    <Input
                                        value={newPlayer.phone_number}
                                        onChange={(e) =>
                                            setNewPlayer((prev) => ({
                                                ...prev,
                                                phone_number: e.target.value,
                                            }))
                                        }
                                        placeholder="Phone Number"
                                    />
                                </Col>
                                <Col md={1} className="text-end">
                                    <Button color="primary" size="sm" onClick={addPlayer}>
                                        Add
                                    </Button>
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Input
                                        type="textarea"
                                        value={newPlayer.notes}
                                        onChange={(e) =>
                                            setNewPlayer((prev) => ({
                                                ...prev,
                                                notes: e.target.value,
                                            }))
                                        }
                                        placeholder="Notes"
                                    />
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>)}
            </CardBody>
        </Card>
    );
};

export default TeamPlayers;
